import {
  authConfigSchema,
  authResponseSchema,
  authUserSchema,
  createdInviteSchema,
  inviteSchema,
  invitePreviewSchema,
  type CreateInvite,
  type CreatedInvite,
  type InvitePreview,
  type AuthConfig,
  type AuthResponse,
  type AuthUser,
  type LoginRequest,
  type RegisterRequest,
} from '@libellus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../../lib/api';

export const authApi = {
  config: (): Promise<AuthConfig> => api.get('/auth/config', { schema: authConfigSchema }),
  login: (body: LoginRequest): Promise<AuthResponse> =>
    api.post('/auth/login', { body, schema: authResponseSchema }),
  register: (body: RegisterRequest): Promise<AuthResponse> =>
    api.post('/auth/register', { body, schema: authResponseSchema }),
  me: (): Promise<AuthUser> => api.get('/auth/me', { schema: authUserSchema }),
  previewInvite: (token: string): Promise<InvitePreview> =>
    api.get(`/auth/invites/${encodeURIComponent(token)}`, { schema: invitePreviewSchema }),
  invites: () => api.get('/invites', { schema: z.array(inviteSchema) }),
  createInvite: (body: CreateInvite): Promise<CreatedInvite> =>
    api.post('/invites', { body, schema: createdInviteSchema }),
  revokeInvite: (id: number): Promise<void> => api.delete(`/invites/${id}`),
};

/** An invitation link's details, for the sign-up form; errors when it no longer works. */
export function useInvitePreview(token: string | null) {
  return useQuery({
    queryKey: ['auth', 'invite', token],
    queryFn: () => authApi.previewInvite(token!),
    enabled: token !== null,
    retry: false,
  });
}

/** Invitations, for super users. */
export function useInvites() {
  return useQuery({ queryKey: ['invites'], queryFn: authApi.invites });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: authApi.createInvite,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['invites'] }),
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: authApi.revokeInvite,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['invites'] }),
  });
}

export function useAuthConfig() {
  return useQuery({ queryKey: ['auth', 'config'], queryFn: authApi.config, staleTime: Infinity });
}
